import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User as UserIcon, MapPin, Lock, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState({});
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/auth/me").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const upd = (k, v) => setData({...data, [k]: v});

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: data.name,
        phone: data.phone,
        address_street: data.address_street,
        address_number: data.address_number,
        address_complement: data.address_complement,
        address_neighborhood: data.address_neighborhood,
        address_city: data.address_city,
        address_state: data.address_state,
        address_zip_code: data.address_zip_code,
      };
      const { data: updated } = await api.patch("/auth/profile", payload);
      setData(updated);
      // Reflect in global auth context (name, phone shown in header)
      if (setUser) setUser({...user, name: updated.name, phone: updated.phone});
      toast.success("Perfil atualizado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao salvar"); }
    finally { setBusy(false); }
  };

  const changePwd = async () => {
    if (!pwd.current_password || !pwd.new_password) { toast.error("Preencha as duas senhas"); return; }
    try {
      await api.post("/auth/change-password", pwd);
      toast.success("Senha alterada");
      setPwd({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  if (loading) return <div className="p-10 text-center text-stone-400"><Loader2 className="animate-spin mx-auto"/></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><UserIcon size={28}/> Meu perfil</h1>
        <p className="text-stone-500 text-sm mt-1">Estes dados são usados automaticamente no checkout.</p>
      </div>

      <Card className="p-6 rounded-2xl border-stone-200">
        <h2 className="font-display text-lg font-semibold mb-4">Dados pessoais</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Nome</Label><Input value={data.name || ""} onChange={(e) => upd("name", e.target.value)} className="rounded-xl mt-1" data-testid="pf-name"/></div>
          <div><Label>E-mail (não editável)</Label><Input value={data.email || ""} disabled className="rounded-xl mt-1 bg-stone-50"/></div>
          <div><Label>Telefone (também aceito como login)</Label><Input value={data.phone || ""} onChange={(e) => upd("phone", e.target.value)} placeholder="(99) 99999-9999" className="rounded-xl mt-1" data-testid="pf-phone"/></div>
          <div><Label>Código da carteira</Label><Input value={data.wallet_code || ""} disabled className="rounded-xl mt-1 bg-stone-50 font-mono"/></div>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200">
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><MapPin size={18}/> Endereço</h2>
        <div className="grid sm:grid-cols-6 gap-4">
          <div className="sm:col-span-4"><Label>Rua / Logradouro</Label><Input value={data.address_street || ""} onChange={(e) => upd("address_street", e.target.value)} className="rounded-xl mt-1" data-testid="pf-street"/></div>
          <div className="sm:col-span-2"><Label>Número</Label><Input value={data.address_number || ""} onChange={(e) => upd("address_number", e.target.value)} className="rounded-xl mt-1"/></div>
          <div className="sm:col-span-3"><Label>Complemento</Label><Input value={data.address_complement || ""} onChange={(e) => upd("address_complement", e.target.value)} className="rounded-xl mt-1"/></div>
          <div className="sm:col-span-3"><Label>Bairro</Label><Input value={data.address_neighborhood || ""} onChange={(e) => upd("address_neighborhood", e.target.value)} className="rounded-xl mt-1"/></div>
          <div className="sm:col-span-3"><Label>Cidade</Label><Input value={data.address_city || ""} onChange={(e) => upd("address_city", e.target.value)} className="rounded-xl mt-1"/></div>
          <div className="sm:col-span-1"><Label>UF</Label><Input maxLength={2} value={data.address_state || ""} onChange={(e) => upd("address_state", e.target.value.toUpperCase())} className="rounded-xl mt-1"/></div>
          <div className="sm:col-span-2"><Label>CEP</Label><Input value={data.address_zip_code || ""} onChange={(e) => upd("address_zip_code", e.target.value)} placeholder="00000-000" className="rounded-xl mt-1"/></div>
        </div>
        <div className="flex justify-end mt-5">
          <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="pf-save">
            <Save size={14} className="mr-1.5"/> {busy ? "Salvando..." : "Salvar perfil"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200">
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Lock size={18}/> Senha</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Senha atual</Label><Input type="password" value={pwd.current_password} onChange={(e) => setPwd({...pwd, current_password: e.target.value})} className="rounded-xl mt-1" data-testid="pf-pwd-current"/></div>
          <div><Label>Nova senha</Label><Input type="password" value={pwd.new_password} onChange={(e) => setPwd({...pwd, new_password: e.target.value})} className="rounded-xl mt-1" data-testid="pf-pwd-new"/></div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={changePwd} variant="outline" className="rounded-xl" data-testid="pf-pwd-change">Alterar senha</Button>
        </div>
      </Card>
    </div>
  );
}
